/**
 * Intraday P&L chart for one bot — the pattern-discovery view.
 *
 * Draws today's position_mark series (seeded from /api/bot/{id}/marks,
 * appended live from /ws/trades) as a line over a zero baseline, green
 * fill above / red below, with an optional reference-price overlay and
 * a marker where the ratchet armed. Pure SVG, no chart library: the
 * series is small (one point per manage tick) and redraws are cheap.
 *
 * IC trail after the 50% target (server PR #130): once the trail arms,
 * the chart adds the original 50% target, a TRAIL ARMED marker, and two
 * step lines — the confirmed peak and the floor (the exit level) — so
 * the floor's whole history is visible. The caller builds the model
 * with ./trail.ts from the marks (+ the day's TRAIL_ARMED event) up to
 * the playhead, so a replay shows what live showed at that moment; held
 * cycles never trail and draw nothing extra.
 *
 * Hovering snaps a crosshair to the nearest mark and shows time, P&L
 * and the underlying's level; the overlay gets its own right-hand axis
 * so the dashed line reads in real prices, not just shape.
 */

import React, { useMemo, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { trailAt, TrailModel } from './trail';

export interface MarkPoint {
  at: string;               // ET ISO timestamp from the server
  unrealizedPnl: number;
  refLast?: number;         // underlying reference price, when the bot sends it
  ratchetArmed?: boolean;
  /** IC marks: the cycle's live profit-target line ($, all contracts). */
  targetPnl?: number;
  holdArmed?: boolean;
  trailArmed?: boolean;
  /** IC trail state as the tick began (null/absent until armed). */
  trailPct?: number | null;
  trailPeakPnl?: number | null;
  trailFloorPnl?: number | null;
}

interface Props {
  points: MarkPoint[];
  height?: number;
  /** Playback head in ET hours (e.g. 13.5 = 13:30). When set, only
   * points at/before it are drawn — axes stay scaled to the FULL day
   * so the picture doesn't jump while the tape replays. */
  cutoffH?: number | null;
  /** Underlying symbol for the hover readout (default QQQ). */
  refSymbol?: string;
  /** IC trail model as of the playhead (buildTrailUpTo), or null. */
  trail?: TrailModel | null;
}

const W = 1000;
const PAD = { l: 52, r: 52, t: 12, b: 20 };
const T0 = 9.5;   // 09:30 ET
const T1 = 16.0;  // 16:00 ET

/** Hours-since-midnight ET from the server's ET ISO stamp. */
const toH = (at: string) => {
  const hh = Number(at.slice(11, 13));
  const mm = Number(at.slice(14, 16));
  const ss = Number(at.slice(17, 19)) || 0;
  return hh + mm / 60 + ss / 3600;
};

const niceCeil = (v: number) => {
  const steps = [50, 100, 200, 250, 500, 1000, 2000, 2500, 5000, 10000, 25000];
  for (const s of steps) if (v <= s) return s;
  return Math.ceil(v / 25000) * 25000;
};

const fmtTick = (v: number) =>
  Math.abs(v) >= 1000 ? `${v > 0 ? '+' : ''}${v / 1000}k` : `${v > 0 ? '+' : ''}${v}`;

const fmtPnl = (v: number) =>
  `${v < 0 ? '−' : '+'}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

interface PlottedPoint {
  x: number;
  y: number;
  ry: number | null;        // overlay y for this mark, when refLast present
  p: MarkPoint;
}

const BotPnlChart: React.FC<Props> = ({
  points, height = 220, cutoffH = null, refSymbol = 'QQQ', trail = null,
}) => {
  const theme = useTheme();
  const [showRef, setShowRef] = useState(true);
  const [hover, setHover] = useState<number | null>(null);

  const hasRef = useMemo(() => points.some((p) => p.refLast != null), [points]);

  const model = useMemo(() => {
    const H = height;
    const xPos = (h: number) => PAD.l + ((h - T0) / (T1 - T0)) * (W - PAD.l - PAD.r);
    // Axis scale from the FULL day even mid-playback.
    const ymax = niceCeil(Math.max(
      100, ...points.map((p) => Math.abs(p.unrealizedPnl)),
      trail?.targetPnl != null ? Math.abs(trail.targetPnl) : 0,
    ));
    const yPos = (v: number) => PAD.t + ((ymax - v) / (2 * ymax)) * (H - PAD.t - PAD.b);

    const visible = cutoffH == null
      ? points
      : points.filter((p) => toH(p.at) <= cutoffH);

    // Reference-price overlay scaled to its own min/max band — band
    // bounds from the FULL day, drawn points clipped to the playhead.
    let rPos: ((v: number) => number) | null = null;
    let refTicks: Array<{ y: number; lab: string }> = [];
    if (hasRef) {
      const band = points.filter((p) => p.refLast != null) as Array<Required<Pick<MarkPoint, 'refLast' | 'at'>>>;
      if (band.length >= 2) {
        const lo = Math.min(...band.map((r) => r.refLast));
        const hi = Math.max(...band.map((r) => r.refLast));
        const span = Math.max(hi - lo, 0.5);
        rPos = (v: number) =>
          PAD.t + ((hi + span * 0.08 - v) / (span * 1.16)) * (H - PAD.t - PAD.b);
        const mid = (hi + lo) / 2;
        refTicks = [hi, mid, lo].map((v) => ({ y: rPos!(v), lab: v.toFixed(2) }));
      }
    }

    const pts: PlottedPoint[] = visible.map((p) => ({
      x: xPos(toH(p.at)),
      y: yPos(p.unrealizedPnl),
      ry: p.refLast != null && rPos ? rPos(p.refLast) : null,
      p,
    }));
    const line = pts.map((q) => `${q.x.toFixed(1)},${q.y.toFixed(1)}`).join(' ');
    const z = yPos(0);
    const area = pts.length
      ? `M${pts[0].x.toFixed(1)},${z} L${line.replace(/ /g, ' L')} L${pts[pts.length - 1].x.toFixed(1)},${z} Z`
      : '';
    const refLine = pts
      .filter((q) => q.ry != null)
      .map((q) => `${q.x.toFixed(1)},${q.ry!.toFixed(1)}`)
      .join(' ');

    // First false→true ratchet transition, if any (only once the
    // playhead has reached it — the replay shouldn't spoil the day).
    let armX: number | null = null;
    for (let i = 1; i < visible.length; i++) {
      if (visible[i].ratchetArmed && !visible[i - 1].ratchetArmed) {
        armX = xPos(toH(visible[i].at));
        break;
      }
    }
    const headX = cutoffH == null ? null : xPos(Math.min(Math.max(cutoffH, T0), T1));

    // Trail overlay. The model is already built from the marks/events at
    // or before the playhead (see Robot.tsx), so it is drawn whole.
    let trailDraw: null | {
      armX: number; endX: number; targetY: number | null;
      peakPath: string; floorPath: string; bandPath: string;
      peakY: number; floorY: number | null; peak: number; floor: number | null;
      exitX: number | null;
    } = null;
    if (trail && trail.steps.length) {
      const steps = trail.steps;
      const endX = xPos(toH(trail.endAt));
      const xs = steps.map((s) => xPos(toH(s.at)));
      const stepPath = (vals: Array<number | null>) => {
        let d = '';
        for (let i = 0; i < steps.length; i++) {
          const v = vals[i];
          if (v == null) continue;
          const y = yPos(v).toFixed(1);
          const x0 = xs[i].toFixed(1);
          const x1 = (i + 1 < steps.length ? xs[i + 1] : endX).toFixed(1);
          d += `${d ? ' L' : 'M'}${x0},${y} H${x1}`;
        }
        return d;
      };
      const peaks = steps.map((s) => s.peak);
      const floors = steps.map((s) => s.floor);
      const peakPath = stepPath(peaks);
      const floorPath = stepPath(floors);
      // Band between floor and peak: peak steps forward, floor back.
      let bandPath = '';
      if (floors.every((f) => f != null)) {
        const fwd = peakPath.replace(/^M/, '');
        const back: string[] = [];
        for (let i = steps.length - 1; i >= 0; i--) {
          const y = yPos(floors[i] as number).toFixed(1);
          const x1 = (i + 1 < steps.length ? xs[i + 1] : endX).toFixed(1);
          back.push(`L${x1},${y} L${xs[i].toFixed(1)},${y}`);
        }
        bandPath = `M${fwd} ${back.join(' ')} Z`;
      }
      const last = steps[steps.length - 1];
      trailDraw = {
        armX: xPos(toH(trail.armAt)), endX,
        targetY: trail.targetPnl != null ? yPos(trail.targetPnl) : null,
        peakPath, floorPath, bandPath,
        peakY: yPos(last.peak), floorY: last.floor != null ? yPos(last.floor) : null,
        peak: last.peak, floor: last.floor,
        exitX: trail.exitAt ? xPos(toH(trail.exitAt)) : null,
      };
    }

    const hours: Array<{ x: number; lab: string }> = [];
    for (let h = 10; h <= 16; h++) {
      hours.push({ x: xPos(h), lab: h > 12 ? `${h - 12}p` : `${h}${h === 12 ? 'p' : 'a'}` });
    }
    const yTicks = [-ymax, -ymax / 2, ymax / 2, ymax].map((v) => ({ y: yPos(v), lab: fmtTick(v) }));
    const last = pts[pts.length - 1] || null;
    return { H, z, line, area, refLine, refTicks, armX, headX, hours, yTicks, last, pts, trailDraw };
  }, [points, height, hasRef, cutoffH, trail]);

  const inkFaint = theme.palette.divider;
  const ink3 = theme.palette.text.disabled;
  const up = theme.palette.success.main;
  const dn = theme.palette.error.main;
  const accent = theme.palette.primary.main;

  if (points.length < 2) {
    return (
      <Box
        sx={{
          height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderTop: 1, borderBottom: 1, borderColor: 'divider', my: 0.5,
        }}
      >
        <Typography variant="caption" color="text.disabled">
          No position marks recorded this day — the chart draws while the bot holds a position.
        </Typography>
      </Box>
    );
  }

  const { H, z, line, area, refLine, refTicks, armX, headX, hours, yTicks, last, pts, trailDraw } = model;
  const trailUp = theme.palette.success.main;
  const trailFloor = theme.palette.error.main;
  const trailMark = theme.palette.secondary.main;
  const trailTarget = theme.palette.text.secondary;
  const clipUp = `clip-up-${Math.round(z)}`;
  const clipDn = `clip-dn-${Math.round(z)}`;

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!pts.length) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const xSvg = ((e.clientX - rect.left) / rect.width) * W;
    let best = 0;
    for (let i = 1; i < pts.length; i++) {
      if (Math.abs(pts[i].x - xSvg) < Math.abs(pts[best].x - xSvg)) best = i;
    }
    setHover(best);
  };

  const hp = hover != null ? pts[hover] : null;
  const hpTrail = hp && trail ? trailAt(trail, hp.p.at) : null;
  const tipLeftPct = hp ? (hp.x / W) * 100 : 0;
  const tipFlip = hp ? hp.x > W * 0.72 : false;

  return (
    <Box sx={{ position: 'relative' }}>
      {hasRef && (
        <FormControlLabel
          sx={{
            position: 'absolute', top: -6, right: 0, m: 0,
            '& .MuiFormControlLabel-label': { fontSize: 11, color: 'text.secondary' },
          }}
          control={
            <Checkbox
              size="small"
              checked={showRef}
              onChange={(e) => setShowRef(e.target.checked)}
              sx={{ py: 0.25 }}
            />
          }
          label="underlying"
        />
      )}
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ display: 'block', width: '100%', height: 'auto' }}
        role="img"
        aria-label="Intraday P&L"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        {yTicks.map((t) => (
          <g key={t.lab}>
            <line x1={PAD.l} x2={W - PAD.r} y1={t.y} y2={t.y} stroke={inkFaint} strokeWidth={0.75} />
            <text x={PAD.l - 6} y={t.y + 3.5} textAnchor="end" fontSize={10.5} fill={ink3} fontFamily="monospace">
              {t.lab}
            </text>
          </g>
        ))}
        {showRef && refLine && refTicks.map((t) => (
          <text
            key={`r-${t.lab}`} x={W - PAD.r + 6} y={t.y + 3.5} textAnchor="start"
            fontSize={10.5} fill={accent} opacity={0.75} fontFamily="monospace"
          >
            {t.lab}
          </text>
        ))}
        {hours.map((h) => (
          <text key={h.lab} x={h.x} y={H - 5} textAnchor="middle" fontSize={10.5} fill={ink3} fontFamily="monospace">
            {h.lab}
          </text>
        ))}
        <line x1={PAD.l} x2={W - PAD.r} y1={z} y2={z} stroke={ink3} strokeWidth={1} />
        <clipPath id={clipUp}><rect x={0} y={0} width={W} height={z} /></clipPath>
        <clipPath id={clipDn}><rect x={0} y={z} width={W} height={H - z} /></clipPath>
        <path d={area} fill={up} opacity={0.12} clipPath={`url(#${clipUp})`} />
        <path d={area} fill={dn} opacity={0.12} clipPath={`url(#${clipDn})`} />
        {showRef && refLine && (
          <polyline points={refLine} fill="none" stroke={accent} strokeWidth={1} strokeDasharray="3 3" opacity={0.55} />
        )}
        {trailDraw && (
          <g pointerEvents="none">
            {trailDraw.targetY != null && (
              <g>
                <line x1={PAD.l} x2={W - PAD.r} y1={trailDraw.targetY} y2={trailDraw.targetY}
                  stroke={trailTarget} strokeWidth={1} strokeDasharray="6 4" opacity={0.55} />
                <text x={PAD.l + 4} y={trailDraw.targetY - 4} fontSize={10} fill={trailTarget} opacity={0.85}>
                  50% TARGET {trail?.targetPnl != null ? fmtPnl(trail.targetPnl) : ''}
                </text>
              </g>
            )}
            {trailDraw.bandPath && (
              <path d={trailDraw.bandPath} fill={trailUp} opacity={0.07} />
            )}
            <path d={trailDraw.peakPath} fill="none" stroke={trailUp} strokeWidth={1.4} opacity={0.9} />
            {trailDraw.floorPath && (
              <path d={trailDraw.floorPath} fill="none" stroke={trailFloor} strokeWidth={1.6} strokeDasharray="5 3" />
            )}
          </g>
        )}
        <polyline points={line} fill="none" stroke={theme.palette.text.primary} strokeWidth={1.6} />
        {armX != null && (
          <g>
            <line x1={armX} x2={armX} y1={PAD.t} y2={H - PAD.b} stroke={theme.palette.warning.main} strokeDasharray="2 3" opacity={0.6} />
            <text x={armX + 4} y={PAD.t + 10} fontSize={10} fill={theme.palette.warning.main} fontWeight={500}>
              RATCHET ARMED
            </text>
          </g>
        )}
        {trailDraw && (() => {
          // End labels: right of the step ends when there is room, else left.
          const room = trailDraw.endX < W - PAD.r - 110;
          const lx = room ? trailDraw.endX + 5 : trailDraw.endX - 5;
          const anchor = room ? 'start' : 'end';
          let py = trailDraw.peakY - 4;
          const fy = trailDraw.floorY != null ? trailDraw.floorY + 11 : null;
          if (fy != null && fy - py < 12) { py = Math.min(py, fy - 12); }
          return (
            <g pointerEvents="none">
              <line x1={trailDraw.armX} x2={trailDraw.armX} y1={PAD.t} y2={H - PAD.b}
                stroke={trailMark} strokeDasharray="2 3" opacity={0.75} />
              <text x={trailDraw.armX + 4} y={PAD.t + 22} fontSize={10} fill={trailMark} fontWeight={600}>
                TRAIL ARMED{trail?.trailPct != null ? ` · ${trail.trailPct}%` : ''}
              </text>
              <text x={lx} y={py} textAnchor={anchor} fontSize={10} fill={trailUp} fontFamily="monospace" fontWeight={600}>
                peak {fmtPnl(trailDraw.peak)}
              </text>
              {fy != null && trailDraw.floor != null && (
                <text x={lx} y={fy} textAnchor={anchor} fontSize={10} fill={trailFloor} fontFamily="monospace" fontWeight={600}>
                  floor {fmtPnl(trailDraw.floor)}
                </text>
              )}
              {trailDraw.exitX != null && (
                <g>
                  <line x1={trailDraw.exitX} x2={trailDraw.exitX} y1={PAD.t} y2={H - PAD.b}
                    stroke={trailFloor} strokeDasharray="2 3" opacity={0.7} />
                  <text x={trailDraw.exitX - 4} y={H - PAD.b - 6} textAnchor="end" fontSize={10} fill={trailFloor} fontWeight={600}>
                    TRAIL EXIT
                  </text>
                </g>
              )}
            </g>
          );
        })()}
        {headX != null && (
          <line x1={headX} x2={headX} y1={PAD.t} y2={H - PAD.b} stroke={accent} strokeWidth={1} opacity={0.5} />
        )}
        {hp && (
          <g pointerEvents="none">
            <line x1={hp.x} x2={hp.x} y1={PAD.t} y2={H - PAD.b} stroke={ink3} strokeWidth={0.75} strokeDasharray="2 2" />
            <circle cx={hp.x} cy={hp.y} r={3.5} fill={hp.p.unrealizedPnl >= 0 ? up : dn} stroke={theme.palette.background.paper} strokeWidth={1} />
            {showRef && hp.ry != null && (
              <circle cx={hp.x} cy={hp.ry} r={3} fill={accent} stroke={theme.palette.background.paper} strokeWidth={1} />
            )}
          </g>
        )}
        {last && (
          <circle cx={last.x} cy={last.y} r={4} fill={last.p.unrealizedPnl >= 0 ? up : dn} />
        )}
      </svg>
      {hp && (
        <Box
          sx={{
            position: 'absolute',
            top: 2,
            left: `${tipLeftPct}%`,
            transform: tipFlip ? 'translateX(calc(-100% - 8px))' : 'translateX(8px)',
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            px: 1,
            py: 0.5,
            pointerEvents: 'none',
            boxShadow: 1,
            whiteSpace: 'nowrap',
            zIndex: 2,
          }}
        >
          <Typography component="div" sx={{ fontSize: 11, fontFamily: 'monospace', color: 'text.secondary' }}>
            {hp.p.at.slice(11, 16)} ET
          </Typography>
          <Typography component="div" sx={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 600, color: hp.p.unrealizedPnl >= 0 ? 'success.main' : 'error.main' }}>
            {fmtPnl(hp.p.unrealizedPnl)}
          </Typography>
          {showRef && hp.p.refLast != null && (
            <Typography component="div" sx={{ fontSize: 11, fontFamily: 'monospace', color: 'primary.main' }}>
              {refSymbol} {hp.p.refLast.toFixed(2)}
            </Typography>
          )}
          {hpTrail && (
            <Typography component="div" sx={{ fontSize: 11, fontFamily: 'monospace', color: 'text.secondary' }}>
              <Box component="span" sx={{ color: 'success.main' }}>peak {fmtPnl(hpTrail.peak)}</Box>
              {hpTrail.floor != null && (
                <> · <Box component="span" sx={{ color: 'error.main' }}>floor {fmtPnl(hpTrail.floor)}</Box></>
              )}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default BotPnlChart;
